import {
  DurationPickerV2,
  InfoPopover,
  Modal,
  NumberField,
  Select,
  TrackedButton,
  Typography,
  useDurationPickerField,
} from '@automata/ui';
import {
  ObjectiveInput,
  OptimisationStageInput,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
import { MenuItem, Stack } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  CARRY_FORWARD_OPTIONS,
  STAGE_TYPE_OPTIONS,
  VALIDATION_MESSAGES,
  isCarryForwardRule,
  isObjectiveInput,
} from './constants';
import { OptimisationStageModalMode } from './types';

const DEFAULT_STAGE: OptimisationStageInput = {
  objective: 'makespan',
  stop_at: {
    max_computation_time: null,
    improvement_timer_limit: 5,
    target_objective: null,
  },
  carry_forward_rule: 'same_or_better',
};

export interface OptimisationStageModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (stage: OptimisationStageInput) => void;
  onDelete?: () => void;
  mode: OptimisationStageModalMode;
  initialData?: OptimisationStageInput;
}

const StageTypesPopover = (
  <div>
    <p>
      The solver iteratively improves the metrics defined for each stage until
      its stopping conditions are met. It then progresses to the next stage,
      applying the configured carry-over rules for metrics from previous stages.
    </p>
    <ul>
      <li>
        <b>Makespan:</b> Set the target maximum run duration for the entire
        workflow.
      </li>
      <li>
        <b>Slack:</b> Adds buffer time around strict time constraints, to make
        the workflow more resilient to delays and interruptions.
      </li>
      <li>
        <b>Consistency:</b> Reduce variation in planned task timings across
        batches.
      </li>
      <li>
        <b>Deadtime:</b> Minimise the time labware spends idle at locations
        without an active task.
      </li>
      <li>
        <b>Timing targets:</b> Minimise deviation from the ideal times for each
        timing target.
      </li>
    </ul>
  </div>
);

const CarryOverRulePopover = (
  <div>
    Defines the allowed variations to this stage’s metric in future stages.
    <ul>
      <li>
        <b>Same or better:</b> Future stages can only maintain or improve this
        stage&apos;s metric.
      </li>
      <li>
        <b>Lock:</b> This stage&apos;s metric must remain unchanged in future
        stages.
      </li>
      <li>
        <b>± Value %:</b> Future stages must keep this stage&apos;s metric
        within the specified percentage range.
      </li>
    </ul>
  </div>
);

export const OptimisationStageModal: FC<OptimisationStageModalProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  mode,
  initialData,
}) => {
  const [formData, setFormData] = useState<OptimisationStageInput>(
    initialData ?? DEFAULT_STAGE
  );

  useEffect(() => {
    if (open) {
      setFormData(initialData ?? DEFAULT_STAGE);
    }
  }, [open, initialData]);

  const [errors, setErrors] = useState<{
    improvementTimerLimit?: string;
  }>({});

  const maxComputeTimePicker = useDurationPickerField((totalSeconds) => {
    setFormData((prev) => ({
      ...prev,
      stop_at: {
        ...prev.stop_at,
        max_computation_time: totalSeconds,
      },
    }));
  }, formData.stop_at?.max_computation_time);

  const targetMakespanPicker = useDurationPickerField((totalSeconds) => {
    setFormData((prev) => ({
      ...prev,
      stop_at: {
        ...prev.stop_at,
        target_objective: totalSeconds,
      },
    }));
  }, formData.stop_at?.target_objective);

  const isAddMode = mode === 'add';
  const isMakespanObjective = formData.objective === 'makespan';

  const title = isAddMode ? 'Add new stage' : 'Edit stage';
  const description = isAddMode
    ? 'Configure the settings for this new optimisation stage.'
    : 'Adjust the settings for this optimisation stage.';

  const handleStageTypeChange = useCallback((objective: ObjectiveInput) => {
    setFormData((prev) => ({
      ...prev,
      objective,
      stop_at: {
        ...prev.stop_at,
        target_objective:
          objective === 'makespan' ? prev.stop_at?.target_objective : null,
      },
    }));
  }, []);

  const handleImprovementTimerLimitChange = useCallback(
    (value: number | null) => {
      const hasError = value !== null && value < 0;
      setErrors((prev) => ({
        ...prev,
        improvementTimerLimit: hasError
          ? VALIDATION_MESSAGES.improvementTimerLimit
          : undefined,
      }));
      setFormData((prev) => ({
        ...prev,
        stop_at: {
          ...prev.stop_at,
          improvement_timer_limit: value ?? 5,
        },
      }));
    },
    []
  );

  const handleCarryForwardRuleChange = useCallback(
    (value: OptimisationStageInput['carry_forward_rule']) => {
      setFormData((prev) => ({
        ...prev,
        carry_forward_rule: value,
      }));
    },
    []
  );

  const hasErrors = useMemo(
    () => Object.values(errors).some(Boolean),
    [errors]
  );

  const handleSave = useCallback(() => {
    if (hasErrors) return;
    onSave(formData);
    onClose();
  }, [formData, hasErrors, onClose, onSave]);

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <Stack spacing={3} mt={1}>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>

        <Select
          label="STAGE TYPE"
          value={formData.objective ?? 'makespan'}
          onChange={(e) => {
            const value = e.target.value;
            if (isObjectiveInput(value)) handleStageTypeChange(value);
          }}
          infoPopover={
            <InfoPopover
              title="Optimisation Stage Types"
              copy={StageTypesPopover}
            />
          }
        >
          {STAGE_TYPE_OPTIONS.map(({ value, label, tooltip }) => (
            <MenuItem key={value} value={value} title={tooltip}>
              {label}
            </MenuItem>
          ))}
        </Select>

        <Stack direction="row" spacing={2}>
          <Stack flex={isMakespanObjective ? 1 : undefined} spacing={0.5}>
            <DurationPickerV2
              label="MAXIMUM COMPUTE TIME"
              hours={maxComputeTimePicker.breakdown.hours}
              minutes={maxComputeTimePicker.breakdown.minutes}
              seconds={maxComputeTimePicker.breakdown.seconds}
              handleUpdateTime={maxComputeTimePicker.handleUpdateTime}
              handleBlurParent={maxComputeTimePicker.handleBlur}
              infoPopover={
                <InfoPopover
                  title="Maximum compute time"
                  copy="The maximum time the solver can spend on this stage. If exceeded, the solver proceeds to the next stage. If the global maximum planning time is exceeded, planning stops and the current solution is used."
                />
              }
            />
          </Stack>

          {isMakespanObjective && (
            <Stack flex={1}>
              <DurationPickerV2
                label="TARGET MAKESPAN"
                hours={targetMakespanPicker.breakdown.hours}
                minutes={targetMakespanPicker.breakdown.minutes}
                seconds={targetMakespanPicker.breakdown.seconds}
                handleUpdateTime={targetMakespanPicker.handleUpdateTime}
                handleBlurParent={targetMakespanPicker.handleBlur}
              />
            </Stack>
          )}
        </Stack>

        <Stack spacing={0.5}>
          <NumberField
            fullWidth
            label="IMPROVEMENT TIMER LIMIT (s)"
            value={formData.stop_at?.improvement_timer_limit ?? 5}
            onChange={handleImprovementTimerLimitChange}
            min={0}
            error={errors.improvementTimerLimit}
          />
          <Typography variant="caption" color="text.secondary">
            Specifies how long the solver may remain in this stage without
            progress.
          </Typography>
        </Stack>

        <Stack spacing={0.5}>
          <Select
            label="CARRY-OVER RULE"
            value={formData.carry_forward_rule ?? 'same_or_better'}
            onChange={(e) => {
              const value = e.target.value;
              if (isCarryForwardRule(value))
                handleCarryForwardRuleChange(value);
            }}
            infoPopover={
              <InfoPopover
                title="Carry-Over Rule"
                copy={CarryOverRulePopover}
              />
            }
          >
            {CARRY_FORWARD_OPTIONS.map(({ value, label, tooltip }) => (
              <MenuItem key={value} value={value} title={tooltip}>
                {label}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary">
            Defines the allowed variations to this stage’s metric in future
            stages.
          </Typography>
        </Stack>

        <Stack direction="row" justifyContent="space-between">
          {!isAddMode && onDelete ? (
            <TrackedButton
              trackLabel="workflowBuilder.optimisationStageModal.Delete"
              color="error"
              variant="outlined"
              onClick={onDelete}
            >
              Delete stage
            </TrackedButton>
          ) : (
            <span />
          )}
          <Stack direction="row" spacing={2}>
            <TrackedButton
              trackLabel="workflowBuilder.optimisationStageModal.Cancel"
              color="primary"
              variant="text"
              onClick={onClose}
            >
              Cancel
            </TrackedButton>
            <TrackedButton
              trackLabel={`workflowBuilder.optimisationStageModal.${
                isAddMode ? 'Add' : 'Save'
              }`}
              color="primary"
              variant="contained"
              onClick={handleSave}
              disabled={hasErrors}
            >
              {isAddMode ? 'Add stage' : 'Save changes'}
            </TrackedButton>
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
};
